import { Injectable, inject, signal } from '@angular/core';
import { ProductService, ProductAppleCareService } from '@core';
import { ProductCart, usdToPoints } from '@models';
// RCP-306: add `switchMap` back together with the commented block in loadProductCapabilities().
import { forkJoin, map, of, catchError, tap, Observable } from 'rxjs';

export interface AppleCareItem {
  id: string;
  productName: string;
  price: number; // Unit price for display
  points?: number; // Calculated points for display
  isAdded: boolean;
  brand: string;
  model: string;
  productId: number;
  lineNumber?: number; // The AppleCare product's lineNumber
  // Actual payment amounts from backend (for totals calculation)
  subtotalPoints?: number; // What was actually paid in points (subtotal)
  subtotalPrice?: number; // What was actually paid in $ (subtotal)
}

export interface ProcessedProduct extends ProductCart {
  hasAppleCare: boolean;
  isEngravable: boolean;
  engravingText: string | null;
  appleCareItem?: AppleCareItem | null;
  featuresDisabledDueToQuantity?: boolean;
}

interface CachedCapabilityData {
  isEngravable?: boolean;
  hasAppleCare?: boolean;
  appleCareItem?: AppleCareItem | null;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductCapabilitiesService {
  private productService = inject(ProductService);
  private productAppleCareService = inject(ProductAppleCareService);

  private appleCareProductIds = signal<number[]>([]);
  private appleCareIdsLoaded = signal<boolean>(false);

  private capabilitiesCache = new Map<string, CachedCapabilityData>();
  private readonly CACHE_TIMEOUT_MS = 10 * 60 * 1000;

  // Line-based maps for cart relationships
  appleCarePresenceMap = signal(new Map<number, boolean>()); // lineNumber -> hasAppleCare
  engravableProductMap = signal(new Map<string, boolean>()); // productId -> isEngravable
  appleCareItemsMap = signal(new Map<number, AppleCareItem>()); // lineNumber -> AppleCareItem

  capabilitiesLoaded = signal(false);

  private isCacheValid(cachedItem: CachedCapabilityData): boolean {
    return Date.now() - cachedItem.timestamp < this.CACHE_TIMEOUT_MS;
  }

  private getCachedCapability(productId: string): CachedCapabilityData | null {
    const cached = this.capabilitiesCache.get(productId);
    if (cached && this.isCacheValid(cached)) {
      return cached;
    } else if (cached) {
      this.capabilitiesCache.delete(productId);
    }
    return null;
  }

  private setCachedCapability(productId: string, data: Partial<CachedCapabilityData>): void {
    const existing = this.capabilitiesCache.get(productId);
    const cachedData: CachedCapabilityData = {
      ...existing,
      ...data,
      timestamp: Date.now(),
    };
    this.capabilitiesCache.set(productId, cachedData);
  }

  private updateCachedCapability(productId: string, updates: Partial<CachedCapabilityData>): void {
    const existing = this.capabilitiesCache.get(productId);
    if (existing && this.isCacheValid(existing)) {
      this.capabilitiesCache.set(productId, { ...existing, ...updates });
    } else {
      this.setCachedCapability(productId, updates);
    }
  }

  private checkEngravableWithCache(productId: string): Observable<{ id: string; isEngravable: boolean }> {
    const cached = this.getCachedCapability(productId);
    if (cached && cached.isEngravable !== undefined) {
      return of({ id: productId, isEngravable: cached.isEngravable });
    }

    return this.productService.getProductVariances(productId).pipe(
      map((vars) => {
        const isEngravable = vars.some((v) => v.controlType === 'Text');
        this.updateCachedCapability(productId, { isEngravable });
        return { id: productId, isEngravable };
      }),
      catchError(() => {
        this.updateCachedCapability(productId, { isEngravable: false });
        return of({ id: productId, isEngravable: false });
      }),
    );
  }

  loadProductCapabilities(
    cartProducts: ProductCart[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- RCP-306, used by the commented block
    includeAvailableAppleCare: boolean = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- RCP-306, used by the commented block
    establishExistingRelationships: boolean = false,
  ): void {
    // RCP-306: engraving and AppleCare are not offered for the event. Skip their checks (one engraving
    // request per cart product, plus the AppleCare lookups) and report empty capabilities right away,
    // so the cart does not wait for them. Restore the block below for events that offer them.
    this.appleCarePresenceMap.set(new Map());
    this.engravableProductMap.set(new Map());
    this.appleCareItemsMap.set(new Map());
    this.capabilitiesLoaded.set(true);

    // this.capabilitiesLoaded.set(false);
    //
    // this.productAppleCareService
    //   .isAppleCareProduct()
    //   .pipe(
    //     tap((appleCareIds) => {
    //       this.appleCareProductIds.set(appleCareIds);
    //     }),
    //     switchMap((appleCareIds) => {
    //       return this.processCapabilitiesWithLineNumbers(
    //         cartProducts,
    //         includeAvailableAppleCare,
    //         appleCareIds,
    //         establishExistingRelationships,
    //       );
    //     }),
    //   )
    //   .subscribe();
  }

  private processCapabilitiesWithLineNumbers(
    cartProducts: ProductCart[],
    includeAvailableAppleCare: boolean,
    appleCareIds: number[],
    establishExistingRelationships: boolean = false,
  ) {
    const appleCareMap = new Map<number, boolean>();
    const engravableMap = new Map<string, boolean>();
    const appleCareItemsMap = new Map<number, AppleCareItem>();

    // Process existing 1:1 AppleCare relationships
    this.processLineBasedAppleCareRelationships(cartProducts, appleCareMap, appleCareItemsMap, appleCareIds);

    const apiCalls$ = [];

    // Engravable checks for unique products
    const uniqueProductIds = [...new Set(cartProducts.map((p) => p.productId))];
    const engravableChecks$ = uniqueProductIds.map((id) => this.checkEngravableWithCache(id.toString()));
    apiCalls$.push(...engravableChecks$);

    // AppleCare availability checks (eligibility only)
    if (includeAvailableAppleCare) {
      const appleCareChecks$ = this.createLineBasedAppleCareAvailabilityChecks(
        cartProducts,
        appleCareMap,
        appleCareItemsMap,
        appleCareIds,
      );
      apiCalls$.push(...appleCareChecks$);
    }

    if (apiCalls$.length > 0) {
      return forkJoin(apiCalls$).pipe(
        tap((results) => {
          const engravableResults = results.slice(0, engravableChecks$.length);
          engravableResults.forEach((res: any) => {
            if (res && res.id) {
              engravableMap.set(res.id.toString(), res.isEngravable);
            }
          });

          this.appleCarePresenceMap.set(appleCareMap);
          this.engravableProductMap.set(engravableMap);
          this.appleCareItemsMap.set(appleCareItemsMap);
          this.capabilitiesLoaded.set(true);
        }),
      );
    } else {
      this.appleCarePresenceMap.set(appleCareMap);
      this.engravableProductMap.set(engravableMap);
      this.appleCareItemsMap.set(appleCareItemsMap);
      this.capabilitiesLoaded.set(true);
      return of(null);
    }
  }

  /**
   * Process 1:1 AppleCare relationships based on lineNumber/relatedLineNumber
   * Each AppleCare product links to exactly one parent product line
   */
  private processLineBasedAppleCareRelationships(
    cartProducts: ProductCart[],
    appleCareMap: Map<number, boolean>,
    appleCareItemsMap: Map<number, AppleCareItem>,
    appleCareIds: number[],
  ): void {
    const appleCareProducts = cartProducts.filter((p) => appleCareIds.includes(p.productId));
    const regularProducts = cartProducts.filter((p) => !appleCareIds.includes(p.productId));

    // Each AppleCare product has 1:1 relationship with parent
    for (const appleCareProduct of appleCareProducts) {
      if (appleCareProduct.relatedLineNumber !== null && appleCareProduct.relatedLineNumber !== undefined) {
        const parentProduct = regularProducts.find((p) => p.lineNumber === appleCareProduct.relatedLineNumber);

        if (parentProduct) {
          appleCareMap.set(parentProduct.lineNumber, true);
          const appleCarePoints = usdToPoints(appleCareProduct.price);

          const appleCareItem: AppleCareItem = {
            id: appleCareProduct.productId.toString(),
            productName: appleCareProduct.name,
            price: appleCareProduct.price,
            points: appleCarePoints,
            isAdded: true,
            brand: appleCareProduct.brandName || 'Apple',
            model: parentProduct.name,
            productId: appleCareProduct.productId,
            lineNumber: appleCareProduct.lineNumber,
          };

          appleCareItemsMap.set(parentProduct.lineNumber, appleCareItem);
        }
      }
    }

    // Mark regular products without AppleCare
    for (const product of regularProducts) {
      if (!appleCareMap.has(product.lineNumber)) {
        appleCareMap.set(product.lineNumber, false);
      }
    }
  }

  /**
   * Check for available AppleCare (eligibility) using relatedProducts API
   * Creates line-specific items but NOT added yet
   */
  private createLineBasedAppleCareAvailabilityChecks(
    cartProducts: ProductCart[],
    appleCareMap: Map<number, boolean>,
    appleCareItemsMap: Map<number, AppleCareItem>,
    appleCareIds: number[],
  ) {
    const regularProducts = cartProducts.filter((p) => !appleCareIds.includes(p.productId));

    return regularProducts.map((product) => {
      // Skip if this line already has AppleCare
      if (appleCareMap.get(product.lineNumber)) {
        return of(null);
      }

      const productIdStr = product.productId.toString();

      // Check eligibility
      const lowerName = product.name.toLowerCase();
      const isAppleDevice =
        product.brandName?.toLowerCase() === 'apple' &&
        (lowerName.includes('iphone') ||
          lowerName.includes('ipad') ||
          lowerName.includes('mac') ||
          lowerName.includes('watch') ||
          lowerName.includes('airpod') ||
          lowerName.includes('beats'));

      if (!isAppleDevice) {
        return of(null);
      }

      // Check cache
      const cached = this.getCachedCapability(productIdStr);
      if (cached && cached.hasAppleCare !== undefined) {
        if (cached.hasAppleCare && cached.appleCareItem) {
          const lineItem: AppleCareItem = {
            ...cached.appleCareItem,
            isAdded: false,
            model: product.name,
          };
          appleCareItemsMap.set(product.lineNumber, lineItem);
        }
        return of(null);
      }

      // Fetch from relatedProducts API (eligibility check)
      return this.productService.getRelatedProducts(productIdStr).pipe(
        map((related) => {
          const match = related.find((r) => r.name?.toLowerCase().includes('applecare'));
          if (match) {
            const appleCarePoints = usdToPoints(match.price);

            const item: AppleCareItem = {
              id: match.productId.toString(),
              productName: match.name,
              price: match.price,
              points: appleCarePoints,
              isAdded: false,
              brand: match.brandName || 'Apple',
              model: product.name,
              productId: match.productId,
            };
            appleCareItemsMap.set(product.lineNumber, item);
            this.updateCachedCapability(productIdStr, { hasAppleCare: true, appleCareItem: item });
          } else {
            this.updateCachedCapability(productIdStr, { hasAppleCare: false, appleCareItem: null });
          }
          return null;
        }),
        catchError(() => {
          this.updateCachedCapability(productIdStr, { hasAppleCare: false, appleCareItem: null });
          return of(null);
        }),
      );
    });
  }

  getAppleCareProductIds(): Observable<number[]> {
    const currentIds = this.appleCareProductIds();
    if (currentIds.length > 0 || this.appleCareIdsLoaded()) {
      return of(currentIds);
    }

    return this.productAppleCareService.isAppleCareProduct().pipe(
      tap((ids) => {
        this.appleCareProductIds.set(ids);
        this.appleCareIdsLoaded.set(true);
      }),
    );
  }

  getAppleCareProductIdsSync(): number[] {
    return this.appleCareProductIds();
  }

  clearExpiredCapabilitiesCache(): void {
    for (const [productId, cached] of this.capabilitiesCache.entries()) {
      if (!this.isCacheValid(cached)) {
        this.capabilitiesCache.delete(productId);
      }
    }
  }

  getCapabilitiesCacheStats(): { cacheSize: number; validEntries: number } {
    let validEntries = 0;
    for (const cached of this.capabilitiesCache.values()) {
      if (this.isCacheValid(cached)) {
        validEntries++;
      }
    }
    return { cacheSize: this.capabilitiesCache.size, validEntries };
  }

  clearAllCaches(): void {
    this.capabilitiesCache.clear();
  }

  resetCapabilities(): void {
    this.appleCarePresenceMap.set(new Map());
    this.engravableProductMap.set(new Map());
    this.appleCareItemsMap.set(new Map());
    this.capabilitiesLoaded.set(false);
    this.capabilitiesCache.clear();
  }

  clearCapabilitiesCache(): void {
    this.capabilitiesCache.clear();
  }

  performGlobalCacheCleanup(): void {
    this.clearExpiredCapabilitiesCache();
  }
}
